import {
	ShareBaseDialog,
	Task,
	TaskAction
} from 'sharebase/domain';
import {
	PhoenixUser,
	isPhoenixUser,
	ShareBaseDocument,
	PermissionType
} from 'phoenix/domain';
import { ToastService, TrackingService } from 'sharebase/services';
import {
	AuthService,
	EnvironmentService,
	ProgressService
} from 'phoenix/services';
import { ExternalSelectable } from 'sharebase/components/userSelect/userSelect';
import { TaskFactory } from 'sharebase/factories';
import { AclDatastore } from 'sharebase/dataStores/aclDatastore';
import { DocumentDataStore } from 'sharebase-app/dataStores/documentDataStore';

require('./create-task-dialog.styles.css');

const template = require('./create-task-dialog.template');
const MIN_COMPLETION_DAYS = 1;
const MAX_COMPLETION_DAYS_FOR_INTERNAL_USERS = 90;

export class CreateTaskDialog extends ShareBaseDialog {
	public static $inject = [
		'$mdDialog',
		'$mdMedia',
		'$q',
		'pxProgressService',
		'pxAuthService',
		'dateFilter',
		'pxEnvironmentService',
		'sbAclDatastore',
		'sbaDocumentDataStore',
		'sbTaskFactory',
		'sbToastService',
		'sbTrackingService',
		'gettextCatalog'
	];

	public createTaskForm;
	public deploymentId = -1;
	public task: Task;
	public taskAssignee: PhoenixUser | ExternalSelectable;
	public document: ShareBaseDocument;

	public allowExternalAssignees = false;
	public maxCompletionDaysForExternalUsers = 0;
	public readonly minTaskDueDate = new Date();
	public maxTaskDueDate = new Date();

	private formSubmitted = false;
	

	constructor(
		$mdDialog,
		$mdMedia,
		private $q: ng.IQService,
		private pxProgressService: ProgressService,
		authService: AuthService,
		private dateFilter: ng.IFilterDate,
		private pxEnvironmentService: EnvironmentService,
		private sbAclDatastore:AclDatastore,
		private sbaDocumentDataStore:DocumentDataStore,
		private sbTaskFactory: TaskFactory,
		private sbToastService: ToastService,
		private sbTrackingService: TrackingService,
		protected gettextCatalog:ng.gettext.gettextCatalog
	) {
		super($mdDialog, template, gettextCatalog.getString('Create a Task'), $mdMedia('xs'));
		this.deploymentId = authService.deploymentId;

		this.minTaskDueDate.setDate(new Date().getDate() + MIN_COMPLETION_DAYS);
		this.maxTaskDueDate.setDate(new Date().getDate() + MAX_COMPLETION_DAYS_FOR_INTERNAL_USERS);
	}

	// This is a copy-pasted version of the md-datepicker default formatDate, but with IE-specific lines added in
	// to account for our decision to represent dates with 2-digit years.
	private _formatDate(date, timezone) {
		if (!date) {
			return '';
		}

		// needed for IE, new Date()/Date.parse() parse 2-digit years xx as 19xx
		if (this.pxEnvironmentService.isIE && date.getFullYear < 2000) {
			date.setFullYear(date.getFullYear() + 100);
		}

		var localeTime = date.toLocaleTimeString();
		var formatDate = date;
		if (date.getHours() === 0 &&
				(localeTime.indexOf('11:') !== -1 || localeTime.indexOf('23:') !== -1)) {
			formatDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 1, 0, 0);
		}

		return this.dateFilter(formatDate, 'M/d/yyyy', timezone);
	}

	public formatDate = this._formatDate.bind(this);

	public open(event: ng.IAngularEvent, document: ShareBaseDocument) {
		this.document = document;
		this.setTaskDefaults();
		this.setExternalUserSettings();
		this.sbTaskFactory.trackTaskEvent('openedTaskCreationDialog');
		return super.open(event);
	}

	private setExternalUserSettings() {
		// Grab user permissions to determine whether or not to allow assigning
		// tasks to external users. If allowed, ensure they can't set 
		// the due date past the "Maximum Link Expiration"
		let externalUserSettingsPromise = this.sbAclDatastore.hasPermission(this.document, PermissionType.SHLINK)
			.then(userCanCreateShareLinks => {
				this.allowExternalAssignees = userCanCreateShareLinks;
				if(!userCanCreateShareLinks) {
					return; 
				}

				this.sbaDocumentDataStore
					.load(this.document.DocumentId)
					.then(()=>this.sbaDocumentDataStore.getCurrentUserAcl())
					.then(acl => this.sbaDocumentDataStore.getLinkRights(acl))
					.then(rights => {
						this.maxCompletionDaysForExternalUsers = rights.maxLinkExpirationDays === null ?
							MAX_COMPLETION_DAYS_FOR_INTERNAL_USERS :
							Math.min(rights.maxLinkExpirationDays, MAX_COMPLETION_DAYS_FOR_INTERNAL_USERS);
					});
			});
		
		this.pxProgressService.enqueue(externalUserSettingsPromise);
	}

	public createTask() {
		if (this.createTaskForm.$valid) {
			this.formSubmitted = true;
			if (isPhoenixUser(this.taskAssignee)) {
				this.task.assigneeId = this.taskAssignee.UserId;
			}
			else {
				this.task.externalAssigneeEmail = this.taskAssignee.emailAddress;	
			}
			
			this.task.save(this.document)
				.then(() => {
					return this.sbToastService.showSimple(this.gettextCatalog.getString('Your task has been created successfully.'));
				})
				.catch(() => {
					return this.sbToastService.showSimple(this.gettextCatalog.getString('An error occurred. Your task was not created.'));
				})
				.finally(() => {
					this.formSubmitted = false;
				});

			this.dismiss();
		}
	}

	public updateMaxTaskDueDate(taskAssignee: PhoenixUser | ExternalSelectable){
		if(!taskAssignee) {
			return;
		}

		let isExternalUser = !isPhoenixUser(taskAssignee);
		let daysToComplete = isExternalUser ? this.maxCompletionDaysForExternalUsers : MAX_COMPLETION_DAYS_FOR_INTERNAL_USERS;

		// explicitly re-assign maxTaskDueDate so UI refreshes
		let now = new Date();
		let dueDay = new Date().setDate(now.getDate() + daysToComplete);
		this.maxTaskDueDate = new Date(dueDay);

		// If the user previously selected a date that is now
		// out of the allowed range, then wipe it out.
		if(this.task.dueDate > this.maxTaskDueDate){
			this.task.dueDate = null;
		}
	}

	private setTaskDefaults() {
		this.task = this.sbTaskFactory.create();
		this.task.dueDate = null;
		this.task.type = 'Approval';

		this.taskAssignee = null;
	}
}	



// WEBPACK FOOTER //
// ./src/sharebase-app/components/create-task-dialog/create-task-dialog.ts